/** Typed client for the Tickerpicker API. Grows in Phase 7. */

export interface HealthResponse {
  status: "ok" | "degraded";
  app: string;
  version: string;
  environment: string;
  schema_complete: boolean;
  checked_at: string;
  database: {
    connected: boolean;
    alembic_revision: string | null;
    tables_present: number | null;
    tables_expected: number;
    error: string | null;
  };
}

export async function fetchHealth(): Promise<HealthResponse> {
  const res = await fetch("/api/health");
  if (!res.ok) {
    throw new Error(`API returned ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as HealthResponse;
}

// ---------------------------------------------------------------------------
// Auth (app/api/routes/auth.py) -- minimal login, no invite/reset (Phase 10).
// ---------------------------------------------------------------------------

export interface LoginResponse {
  token: string;
  user_id: string;
  role: "admin" | "user";
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      // body wasn't JSON -- keep statusText
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// PUBLIC landing-page teasers (Group B, 2026-09-06, +1 added 2026-09-22) --
// pre-login, no token. Four unauthenticated data endpoints in the API
// (app/api/routes/market.py /flows and /universe-stats, instrument.py
// /{id}/signal, models.py /scorecard) -- deliberate exceptions since the
// landing page has no token to send. Same honesty rules as their
// authenticated counterparts (rank-space sigma, gaps, small-n) -- never
// called with a token, never gated.
// ---------------------------------------------------------------------------

export interface PublicFlowsDto {
  available: boolean;
  note: string;
  date: string | null;
  fii_net: number | null;
  dii_net: number | null;
  /** Real recent-days trend (2026-09-22) -- the SAME FlowsPointDto shape
   * the authenticated Market page's flows_series uses (vix_change/z-scores/
   * rolling-correlation left null here, genuinely not computed for this
   * lightweight teaser), so the same FlowsChart component renders both. */
  recent: FlowsPointDto[];
}

export async function fetchPublicFlows(): Promise<PublicFlowsDto> {
  const res = await fetch("/api/market/flows");
  return unwrap<PublicFlowsDto>(res);
}

/** The hero stat row's real numbers (2026-09-22) -- replaces hardcoded
 * static text that was found live to have drifted materially wrong. */
export interface UniverseStatsDto {
  stock_count: number;
  bars_count: number;
  history_start_year: number | null;
  data_as_of: string | null;
}

export async function fetchUniverseStats(): Promise<UniverseStatsDto> {
  const res = await fetch("/api/market/universe-stats");
  return unwrap<UniverseStatsDto>(res);
}

/** A NON-PRIMARY horizon's own clean blend (e.g. the 5-day view when the
 * stock's primary/plurality horizon is 3-day) -- never merged into the
 * primary `signal`. See backend/app/factory/coverage.py's HorizonSignal.
 * LIST-shaped contributing_segments -- matches instrument.py/CoverageOut's
 * own convention (segment_key inline per entry). */
/** A contributing segment's share of BOTH cross-segment blends (2026-09-21
 * dual-weight signal): `weight` is the equal-weight share (1/n, unchanged),
 * `weight_skill` is its shrunk IC-weighted share, `ic` is the live trailing
 * rank-IC that weight was computed from (null if not scored yet). */
export interface ContributingSegmentEntry {
  segment_key: string;
  model_id: string;
  weight: number;
  weight_skill: number;
  ic: number | null;
  predicted_value: number;
}

export interface HorizonSignalDto {
  horizon: number;
  signal: number;
  signal_skill_weighted: number | null;
  contributing_segments: ContributingSegmentEntry[];
  signal_as_of: string | null;
}

/** Same as HorizonSignalDto but DICT-shaped contributing_segments, matching
 * app/api/routes/coverage.py's admin-resolver convention (keyed by
 * segment_key rather than carrying it inline per entry). */
export interface HorizonSignalDictDto {
  horizon: number;
  signal: number;
  signal_skill_weighted: number | null;
  contributing_segments: Record<string, { model_id: string; weight: number; weight_skill: number; ic: number | null; predicted_value: number }>;
  signal_as_of: string | null;
}

export interface PublicCoverageOutDto {
  output_type: string;
  signal: number | null;
  signal_skill_weighted: number | null;
  skill_delta: number | null;
  confidence: null;
  scale_note: string;
  matched_horizon: number | null;
  contributing_segments: ContributingSegmentEntry[];
  gaps: CoverageGapDto[];
  near_earnings: boolean;
  days_since_earnings: number | null;
  signal_as_of: string | null;
  secondary_horizons: HorizonSignalDto[];
  conviction: "high" | "mixed" | "single" | null;
}

export interface PublicSignalDto {
  security_id: string;
  display_ticker: string | null;
  company_name: string | null;
  sector_key: string | null;
  as_of: string;
  coverage_return: PublicCoverageOutDto;
  coverage_volatility: PublicCoverageOutDto;
}

export async function fetchPublicSignal(securityId: string): Promise<PublicSignalDto> {
  const res = await fetch(`/api/instrument/${encodeURIComponent(securityId)}/signal`);
  return unwrap<PublicSignalDto>(res);
}

/** The landing page's Signal teaser (2026-09-22): the real, currently
 * best-covered highest-signal stock, picked server-side and cached for
 * the day -- not the same hardcoded ticker every visit. See
 * instrument.py's `_pick_featured_stock` for the selection rule. */
export async function fetchFeaturedSignal(): Promise<PublicSignalDto> {
  const res = await fetch("/api/instrument/featured/signal");
  return unwrap<PublicSignalDto>(res);
}

export interface ScorecardRowDto {
  model_id: string;
  version: string;
  level: string;
  target_ref: string;
  output_type: string;
  horizon: number | null;
  metric_name: string;
  backtest_mean: number;
  backtest_n_folds: number;
  backtest_n_positive: number;
  live_metric_value: number;
  live_sample_n: number;
  live_small_n: boolean;
  verdict: string;
}

export interface ScorecardDto {
  as_of: string;
  caveat: string;
  rows: ScorecardRowDto[];
}

export async function fetchPublicScorecard(): Promise<ScorecardDto> {
  const res = await fetch("/api/models/scorecard");
  return unwrap<ScorecardDto>(res);
}

// --- Market Myths: Tested -- public (no-auth) evidence reader
// (app/api/routes/research_evidence.py). Whitelisted by slug only; the
// anti-drift guard means this returns the EXACT internal doc, not a
// separate public-facing copy that could silently diverge.
export interface ResearchEvidenceDto {
  slug: string;
  title: string;
  markdown: string;
}

export async function fetchResearchEvidence(slug: string): Promise<ResearchEvidenceDto> {
  const res = await fetch(`/api/research-evidence/${slug}`);
  return unwrap<ResearchEvidenceDto>(res);
}

// --- Living/interactive research articles (research_cache, the shared
// scheduled-refresh framework). A different shape from the static
// markdown evidence docs above -- `result` is article-specific JSON, not
// prose.
export interface LiveArticleDto {
  slug: string;
  result: Record<string, unknown>;
  computed_at: string;
  cadence_days: number;
  stale: boolean;
  last_run_failed: boolean;
}

export async function fetchLiveArticle(slug: string): Promise<LiveArticleDto> {
  const res = await fetch(`/api/research-evidence/${slug}/live`);
  return unwrap<LiveArticleDto>(res);
}

// --- Invite / reset (Group B, 2026-09-06) -- public where the landing page
// needs them (request, accept, reset), admin-only for provision. See
// app/api/routes/invite.py for the full flow shape and the honest
// no-SMTP-config fallback (email_sent / owner_notified always reported,
// never assumed).
export interface InviteRequestResponseDto {
  request_id: string;
  owner_notified: boolean;
}

export async function requestInvite(name: string, email: string, useCase: string): Promise<InviteRequestResponseDto> {
  const res = await fetch("/api/invite/request", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, use_case: useCase }),
  });
  return unwrap<InviteRequestResponseDto>(res);
}

export interface SessionResponseDto {
  token: string;
  user_id: string;
  role: string;
}

export async function acceptInvite(token: string, password: string): Promise<SessionResponseDto> {
  const res = await fetch("/api/invite/accept", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  return unwrap<SessionResponseDto>(res);
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  const res = await fetch("/api/reset/request", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return unwrap<{ message: string }>(res);
}

export async function confirmPasswordReset(token: string, password: string): Promise<SessionResponseDto> {
  const res = await fetch("/api/reset/confirm", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  return unwrap<SessionResponseDto>(res);
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  return unwrap<LoginResponse>(res);
}

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

// ---------------------------------------------------------------------------
// Stocks list/filter (app/api/routes/stocks.py, data_spec S9.7 + S10.13).
// Mirrors the backend's Pydantic models exactly -- see FilterRequest/
// FilterResponse/StockRow/Condition/ConditionGroup there.
// ---------------------------------------------------------------------------

export type FilterOperator = "eq" | "gt" | "gte" | "lt" | "lte" | "in";

export interface Condition {
  field: string;
  operator: FilterOperator;
  value: string | number | boolean | (string | number)[];
}

export interface ConditionGroup {
  op: "AND" | "OR";
  conditions: Condition[];
}

export interface FilterRequest {
  groups: ConditionGroup[];
  group_op: "AND" | "OR";
  include_signal: boolean;
  signal_output_type: "return" | "volatility";
  /** Which registered `computed` feature columns to return per row, beyond
   * whatever a filter condition already forces in server-side -- the
   * Columns menu's visible tech/risk fields. */
  extra_columns: string[];
  /** GLOBAL sort, applied server-side to the full matched set before
   * pagination (2026-09-14 fix) -- a dedicated column, "signal", or any
   * registered computed feature. Sorting client-side on only the current
   * page was the exact bug this replaced: a later page could (and did)
   * contain a lower/higher value than the "sorted" first page ever showed,
   * since the two pages' rows were never compared against each other. */
  sort_key: string | null;
  sort_dir: "asc" | "desc";
  /** GLOBAL free-text search, applied server-side to the full matched set
   * before pagination (2026-09-22 fix, same shape as sort_key above) --
   * searching client-side on only the current page was the exact bug this
   * replaced (searching "wipro" found nothing because Wipro wasn't on the
   * loaded page). */
  search?: string | null;
  limit: number;
  offset: number;
}

export interface CoverageGapDto {
  segment_key: string;
  reason: string;
  /** "not_modelled" (default -- nobody has built/attempted this segment),
   * "tested_no_signal" (a real build attempt confirmed no forecastable
   * signal -- a RESOLVED negative, not an omission), or "no_prediction"
   * (a champion exists but hasn't scored this stock for this date yet). */
  gap_type?: "not_modelled" | "tested_no_signal" | "no_prediction";
}

export interface StockRow {
  security_id: string;
  display_ticker: string | null;
  sector_key: string | null;
  cap_tier: string | null;
  close: number | null;
  /** Any requested/filtered computed feature (rsi_14, adx_14, beta_nifty_252,
   * ...), keyed by field_key -- one generic bag, since the registry has 33
   * of these and grows by data entry, not by a response-shape change. */
  features: Record<string, number | null>;
  //: The EQUAL-weight blend -- unchanged since before the 2026-09-21
  //: dual-weight build. `signal_equal` carries the exact same value under
  //: its new, explicit, registered-filterable name.
  signal: number | null;
  signal_equal: number | null;
  //: The SHRUNK IC-weighted blend (2026-09-21) -- shown alongside `signal`,
  //: never replacing it. See coverage.py's SKILL_WEIGHT_LAMBDA comment.
  signal_skill_weighted: number | null;
  //: signal_skill_weighted - signal_equal, precomputed.
  skill_delta: number | null;
  signal_scale_note: string | null;
  signal_gaps: CoverageGapDto[];
  // The stalest contributing champion's own run_date -- can legitimately
  // lag `data_as_of` (FilterResponse) if predict ran behind ingest on some
  // prior day. null wherever `signal` is null.
  signal_as_of: string | null;
  // The PRIMARY (plurality) horizon this signal was blended at, e.g. 3 for
  // a 3-day prediction -- labels the score ("3d") in the list.
  signal_horizon: number | null;
  // "high" | "mixed" | "single" | null -- cross-horizon directional
  // agreement only, never magnitude. See coverage.py's CoverageResolution.
  conviction: "high" | "mixed" | "single" | null;
  //: This stock's own premium-band signal vs ITS OWN normal premium
  //: (2026-09-27, tiered 2026-09-28) -- daily-cached, null for stocks
  //: with under 4 confirmed quarterly earnings reports (66% of the
  //: universe has zero) or flagged by the EPS-source cross-check. Never
  //: an absolute scale -- see app/factory/stock_cape.py.
  stock_relative_cape: number | null;
  stock_cape_classification: "cheap" | "fair" | "expensive" | null;
  //: "tier1" | "tier2" | "tier3" | null -- which method/confidence
  //: produced stock_relative_cape. Display-only.
  stock_cape_tier: "tier1" | "tier2" | "tier3" | null;
}

export interface FilterResponse {
  as_of: string;
  data_as_of: string | null;
  // Calendar/pipeline-schedule-aware freshness reference -- use THIS for
  // AsOfBadge's `reference`, never `as_of` (which is the raw requested
  // date, e.g. literal "today" in Live mode -- comparing against it flags
  // every block "stale" before the daily pipeline's 19:00 IST run, or all
  // weekend).
  expected_as_of: string;
  total_universe: number;
  matched: number;
  coverage_report: string;
  rows: StockRow[];
}

export async function fetchStocks(
  token: string,
  body: FilterRequest,
  asOf?: string,
): Promise<FilterResponse> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const res = await fetch(`/api/stocks${qs}`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return unwrap<FilterResponse>(res);
}

// ---------------------------------------------------------------------------
// Filterable-fields registry (app/api/routes/fields.py, data_spec S9.7).
// Replaces the static fields.ts mirror -- the field picker now discovers
// whatever is actually in the registry, not a hardcoded snapshot of it.
// ---------------------------------------------------------------------------

export type FieldKind = "identity" | "fact" | "tech" | "fund" | "model";
export type FieldDataType = "number" | "percent" | "enum" | "bool" | "text";

export interface FilterableFieldDto {
  field_key: string;
  label: string;
  kind: FieldKind | null;
  data_type: FieldDataType | null;
  applies_to: string[];
  source: "computed" | "column" | "projection" | null;
  provenance: string | null;
  filterable: boolean;
  sortable: boolean;
}

export async function fetchFilterableFields(
  token: string,
  appliesTo?: string,
): Promise<FilterableFieldDto[]> {
  const qs = appliesTo ? `?applies_to=${encodeURIComponent(appliesTo)}` : "";
  const res = await fetch(`/api/filterable-fields${qs}`, { headers: authHeaders(token) });
  return unwrap<FilterableFieldDto[]>(res);
}

// ---------------------------------------------------------------------------
// Enum-value lookup (app/api/routes/stocks.py -- GET /stocks/enum-values).
// ---------------------------------------------------------------------------

export async function fetchEnumValues(
  token: string,
  field: string,
  asOf?: string,
): Promise<string[]> {
  const params = new URLSearchParams({ field });
  if (asOf) params.set("as_of", asOf);
  const res = await fetch(`/api/stocks/enum-values?${params}`, { headers: authHeaders(token) });
  return unwrap<string[]>(res);
}

// ---------------------------------------------------------------------------
// saved_filters / saved_views (app/api/routes/saved.py, data_spec 7.1/9.6).
// User-scoped server-side -- replaces the localStorage fallback.
// ---------------------------------------------------------------------------

export interface SavedFilterTree {
  groups: ConditionGroup[];
  group_op: "AND" | "OR";
  signal_output_type: "return" | "volatility";
}

export interface SortSpec {
  key: string;
  dir: "asc" | "desc";
}

export interface SavedFilterDto {
  filter_id: string;
  name: string;
  filter_tree: SavedFilterTree;
  sort_spec: SortSpec | null;
  created_at: string;
}

export async function createSavedFilter(
  token: string,
  name: string,
  filterTree: SavedFilterTree,
  sortSpec: SortSpec | null,
): Promise<SavedFilterDto> {
  const res = await fetch("/api/saved-filters", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ name, filter_tree: filterTree, sort_spec: sortSpec }),
  });
  return unwrap<SavedFilterDto>(res);
}

/** Edits a strategy IN PLACE (same filter_id) -- since the conditions may
 * have changed, the backend wipes that strategy's cached monthly
 * leaderboard data (results + candidate ranking cache) so nothing stale
 * lingers under the old definition. */
export async function updateSavedFilter(
  token: string,
  filterId: string,
  name: string,
  filterTree: SavedFilterTree,
  sortSpec: SortSpec | null,
): Promise<SavedFilterDto> {
  const res = await fetch(`/api/saved-filters/${encodeURIComponent(filterId)}`, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify({ name, filter_tree: filterTree, sort_spec: sortSpec }),
  });
  return unwrap<SavedFilterDto>(res);
}

export async function listSavedFilters(token: string): Promise<SavedFilterDto[]> {
  const res = await fetch("/api/saved-filters", { headers: authHeaders(token) });
  return unwrap<SavedFilterDto[]>(res);
}

export async function deleteSavedFilter(token: string, filterId: string): Promise<void> {
  const res = await fetch(`/api/saved-filters/${encodeURIComponent(filterId)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!res.ok && res.status !== 204) {
    await unwrap(res); // throws with the real detail
  }
}

// ---------------------------------------------------------------------------
// Strategy Backtest Simulator (app/api/routes/strategy_sim.py -- POST
// /strategy-sim/run). Re-runs a saved strategy across the M most recent
// trading days: top-N by signal, held H days, scored with the same
// Realized/Max Realization convention the Stocks page uses.
// ---------------------------------------------------------------------------

export interface StrategySimStockPick {
  security_id: string;
  display_ticker: string | null;
  predicted_signal: number | null;
  realized_value: number | null;
  max_realized_value: number | null;
}

export interface StrategySimDayResult {
  entry_date: string;
  exit_date: string;
  forced_early_exit: boolean;
  avg_realized: number | null;
  avg_max_realized: number | null;
  picks: StrategySimStockPick[];
}

export interface WinRateStat {
  count: number;
  total: number;
  probability: number | null;
}

/** Thresholds the dropdown offers: "probability of a day's return >= X%".
 * 0 = plain positive-return probability. */
export const WIN_RATE_THRESHOLDS = Array.from({ length: 11 }, (_, i) => i);

export interface StrategySimSummary {
  days_run: number;
  avg_realized: number | null;
  avg_max_realized: number | null;
  best_realized_date: string | null;
  best_realized_value: number | null;
  worst_realized_date: string | null;
  worst_realized_value: number | null;
  best_max_realized_date: string | null;
  best_max_realized_value: number | null;
  worst_max_realized_date: string | null;
  worst_max_realized_value: number | null;
  realized_win_rates: Record<string, WinRateStat>;
  max_realized_win_rates: Record<string, WinRateStat>;
}

export interface StrategySimResponse {
  strategy_name: string;
  top_n: number;
  horizon_days: number;
  lookback_days: number;
  sort_by: "signal_equal" | "signal_skill_weighted";
  summary: StrategySimSummary;
  days: StrategySimDayResult[];
}

export async function runStrategySim(
  token: string,
  filterId: string,
  topN: number,
  horizonDays: number,
  lookbackDays: number,
  sortBy: "signal_equal" | "signal_skill_weighted",
): Promise<StrategySimResponse> {
  const res = await fetch("/api/strategy-sim/run", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      filter_id: filterId,
      top_n: topN,
      horizon_days: horizonDays,
      lookback_days: lookbackDays,
      sort_by: sortBy,
    }),
  });
  return unwrap<StrategySimResponse>(res);
}

// Monthly leaderboard (2026-09-25) -- a permanently CACHED comparison
// across all of a user's saved strategies, computed by a monthly cron
// (never on the request path -- see app/factory/monthly_strategy_sim.py).
// These calls only ever READ; they never trigger a computation.

export interface MonthlyConfigDto {
  top_n: number;
  horizon_days: number;
  sort_by: "signal_equal" | "signal_skill_weighted";
}

export async function getMonthlyConfig(token: string): Promise<MonthlyConfigDto> {
  const res = await fetch("/api/strategy-sim/monthly-config", { headers: authHeaders(token) });
  return unwrap<MonthlyConfigDto>(res);
}

export interface MonthlyConfigSaveResponse extends MonthlyConfigDto {
  job_id: string;
}

export async function setMonthlyConfig(token: string, config: MonthlyConfigDto): Promise<MonthlyConfigSaveResponse> {
  const res = await fetch("/api/strategy-sim/monthly-config", {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify(config),
  });
  return unwrap<MonthlyConfigSaveResponse>(res);
}

export interface RepriceStatus {
  status: "running" | "done" | "error";
  error: string | null;
}

export async function getRepriceStatus(token: string, jobId: string): Promise<RepriceStatus> {
  const res = await fetch(`/api/strategy-sim/reprice-status/${encodeURIComponent(jobId)}`, {
    headers: authHeaders(token),
  });
  return unwrap<RepriceStatus>(res);
}

export interface MonthlyLeaderboardRow {
  filter_id: string;
  strategy_name: string;
  month: string;
  top_n: number;
  horizon_days: number;
  sort_by: string;
  is_final: boolean;
  days_run: number;
  avg_realized: number | null;
  avg_max_realized: number | null;
  best_realized_date: string | null;
  best_realized_value: number | null;
  worst_realized_date: string | null;
  worst_realized_value: number | null;
  computed_at: string;
  realized_win_rates: Record<string, WinRateStat>;
  max_realized_win_rates: Record<string, WinRateStat>;
}

export async function getMonthlyLeaderboard(token: string, month: string): Promise<MonthlyLeaderboardRow[]> {
  const res = await fetch(`/api/strategy-sim/monthly?month=${encodeURIComponent(month)}`, {
    headers: authHeaders(token),
  });
  return unwrap<MonthlyLeaderboardRow[]>(res);
}

export interface MonthlyDetailResponse {
  strategy_name: string;
  month: string;
  top_n: number;
  horizon_days: number;
  sort_by: string;
  is_final: boolean;
  days: StrategySimDayResult[];
}

export async function getMonthlyDetail(token: string, filterId: string, month: string): Promise<MonthlyDetailResponse> {
  const res = await fetch(
    `/api/strategy-sim/monthly/detail?filter_id=${encodeURIComponent(filterId)}&month=${encodeURIComponent(month)}`,
    { headers: authHeaders(token) },
  );
  return unwrap<MonthlyDetailResponse>(res);
}

export interface DefaultViewDto {
  table_context: string;
  columns: string[] | null;
  sort_spec: SortSpec | null;
}

export async function getDefaultView(token: string, tableContext: string): Promise<DefaultViewDto> {
  const res = await fetch(`/api/saved-views/default?table_context=${encodeURIComponent(tableContext)}`, {
    headers: authHeaders(token),
  });
  return unwrap<DefaultViewDto>(res);
}

export async function setDefaultView(
  token: string,
  tableContext: string,
  columns: string[],
  sortSpec: SortSpec | null,
): Promise<DefaultViewDto> {
  const res = await fetch("/api/saved-views/default", {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify({ table_context: tableContext, columns, sort_spec: sortSpec }),
  });
  return unwrap<DefaultViewDto>(res);
}

// ---------------------------------------------------------------------------
// Backtest (app/api/routes/stocks.py -- POST /stocks/backtest).
// ---------------------------------------------------------------------------

export interface BacktestSegmentOutcome {
  segment_key: string;
  model_id: string;
  horizon: number;
  run_date: string;
  resolve_date: string;
  predicted_value: number;
  resolved: boolean;
  realized_value: number | null;
  error: number | null;
}

/** Horizon-matched realized return for the stock's own resolved coverage signal
 * (return-type only) -- a default, always-present column, distinct from the
 * per-segment `segments` list above. "resolved" is honest: it compares
 * resolve_date against the real data frontier, not whether a prediction_outcome
 * row happens to exist yet. */
export interface SignalRealizedOut {
  horizon: number;
  predicted_value: number;
  resolve_date: string;
  resolved: boolean;
  realized_value: number | null;
}

/** The best return reached at any point in the SAME window signal_realized
 * scores, not just the value sitting at its end -- "best case if you'd sold
 * at the peak." `partial: true` means the horizon hasn't fully elapsed yet
 * (capped at today's data frontier), so this is the best point reached SO
 * FAR, not necessarily the eventual peak for the full horizon. */
export interface MaxRealizedOut {
  value: number;
  partial: boolean;
}

export interface BacktestRow {
  security_id: string;
  segments: BacktestSegmentOutcome[];
  signal_realized: SignalRealizedOut | null;
  max_realized: MaxRealizedOut | null;
}

export interface BacktestResponse {
  as_of: string;
  rows: BacktestRow[];
}

export async function fetchBacktest(
  token: string,
  securityIds: string[],
  asOf: string,
  signalOutputType: "return" | "volatility",
  overrideHorizon?: number | null,
): Promise<BacktestResponse> {
  const res = await fetch(`/api/stocks/backtest?as_of=${encodeURIComponent(asOf)}`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      security_ids: securityIds,
      signal_output_type: signalOutputType,
      override_horizon: overrideHorizon ?? null,
    }),
  });
  return unwrap<BacktestResponse>(res);
}

// ---------------------------------------------------------------------------
// Instrument detail (app/api/routes/instrument.py -- GET /instrument/{id}/detail).
// Mirrors the backend's Pydantic response exactly.
// ---------------------------------------------------------------------------

export interface InstrumentBar {
  date: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
}

export interface CaMarkerDto {
  ex_date: string;
  status: string;
  observed_ratio: number | null;
}

export interface EarningsMarkerDto {
  announce_date: string;
}

export interface EarningsDetailDto {
  fiscal_period: string | null;
  announce_date: string;
  reported_eps: number | null;
  analyst_eps_estimate: number | null;
  surprise_pct: number | null;
  is_provisional: boolean | null;
}

export interface MacroMarkerDto {
  event_date: string;
  event_type: string | null;
  description: string | null;
}

export type ContributingSegmentDto = ContributingSegmentEntry;

/** confidence is always `null` -- the cross-segment blend never reports
 * agreement/dispersion by design (that's the within-slot ensemble's
 * concept, F6.6, not this blend's G3). Typed as the literal `null` so a
 * future accidental number would be a type error, not a silent drift. */
export interface CoverageDetailDto {
  output_type: "return" | "volatility";
  //: The EQUAL-weight blend -- unchanged baseline.
  signal: number | null;
  //: The SHRUNK IC-weighted blend (2026-09-21) -- shown alongside `signal`.
  signal_skill_weighted: number | null;
  //: signal_skill_weighted - signal, precomputed.
  skill_delta: number | null;
  confidence: null;
  scale_note: string;
  matched_horizon: number | null;
  contributing_segments: ContributingSegmentDto[];
  gaps: CoverageGapDto[];
  near_earnings: boolean;
  days_since_earnings: number | null;
  // Stalest contributing champion's own run_date -- this signal's own
  // freshness date, separate from the page's `as_of`. null if no signal.
  signal_as_of: string | null;
  secondary_horizons: HorizonSignalDto[];
  conviction: "high" | "mixed" | "single" | null;
}

/** RISK/RANGE context (2026-09-25) -- separate from coverage_return/
 * coverage_volatility. Volatility-tier membership carries NO forecastable
 * RETURN-direction signal (see coverage_return's gaps, gap_type
 * "tested_no_signal"), but the stock's actual magnitude of movement is
 * real, useful information regardless -- how much it moves, not which way.
 * sigma_annual/window are the SAME real stored vol_10d/20d/60d feature
 * Simulation Workbench feeds to its GBM engine, never the rank-space
 * fitted sigma (coverage_volatility.signal). */
export interface IndexTrackDto {
  persistence_sigma_annual: number | null;
  persistence_window_days: number;
  horizon_days: number;
  neutral_drift_note: string;
  regime: { status?: string; note?: string; as_of_date?: string; monitored_qlike?: number; sample_n?: number };
  fan_days: number[];
  fan: Record<string, number[]>;
}

export interface VolatilityContextDto {
  available: boolean;
  tier: string | null;
  tier_label: string | null;
  window: number;
  sigma_annual: number | null;
  as_of_date: string | null;
  horizon_days: number | null;
  //: sqrt-time-scaled expected move over horizon_days, as a %. A
  //: MAGNITUDE (how far it could move either way), never a direction.
  expected_move_pct: number | null;
}

export interface FundamentalsDto {
  available: boolean;
  note: string;
  rows: { report_date: string; line_item: string; value: string }[];
}

export interface NewsItemDto {
  title: string;
  source_name: string | null;
  published_at: string;
  url: string | null;
}

export interface ReclassificationDto {
  dimension: string;
  from_segment: string | null;
  to_segment: string;
  changed_on: string;
  consequence: string | null;
}

export interface RsiPointDto {
  date: string;
  value: number;
}

export interface LlmNoteDto {
  text: string | null;
  register: "TENTATIVE" | "CONFLICTED" | "CONFIDENT" | null;
}

export interface InstrumentDetailDto {
  security_id: string;
  display_ticker: string | null;
  company_name: string | null;
  isin: string | null;
  primary_market: string | null;
  sector_key: string | null;
  cap_tier: string | null;
  index_groups: string[];
  as_of: string;
  // Calendar/pipeline-schedule-aware freshness reference -- use for
  // AsOfBadge's `reference`, never `as_of` itself (the raw requested
  // viewing date, unresolved against real data -- comparing badges
  // against it flags every block "stale" before the pipeline has run).
  expected_as_of: string;
  bars: InstrumentBar[];
  rsi_series: RsiPointDto[];
  technicals: Record<string, number | null>;
  technicals_as_of: string | null;
  technicals_source: "stored" | "computed_on_demand";
  ca_markers: CaMarkerDto[];
  earnings_markers: EarningsMarkerDto[];
  earnings_detail: EarningsDetailDto[];
  macro_markers: MacroMarkerDto[];
  coverage_return: CoverageDetailDto;
  coverage_volatility: CoverageDetailDto;
  /** Deterministic, zero-LLM templated prose (2026-10-25) -- supplementary
   * to the numeric coverage panels, never a replacement. `null` when
   * there's no signal to describe. `register` is TENTATIVE/CONFLICTED/
   * CONFIDENT, chosen deterministically, never by model discretion (there
   * is no model -- this is platform-generated, not LLM-generated). */
  llm_note_return: LlmNoteDto | null;
  llm_note_volatility: LlmNoteDto | null;
  llm_note_synthesis: LlmNoteDto | null;
  volatility_context: VolatilityContextDto;
  /** Indices only (null for stocks) -- see backend IndexTrackOut. */
  index_track: IndexTrackDto | null;
  fundamentals: FundamentalsDto;
  news: NewsItemDto[];
  news_count: number;
  reclassifications: ReclassificationDto[];
}

export async function fetchInstrumentDetail(
  token: string,
  securityId: string,
  asOf?: string,
  barsLookbackDays = 180,
): Promise<InstrumentDetailDto> {
  const params = new URLSearchParams({ bars_lookback_days: String(barsLookbackDays) });
  if (asOf) params.set("as_of", asOf);
  const res = await fetch(`/api/instrument/${encodeURIComponent(securityId)}/detail?${params}`, {
    headers: authHeaders(token),
  });
  return unwrap<InstrumentDetailDto>(res);
}

export interface NewsRefreshResultDto {
  attempted: number;
  written: number;
  skipped: number;
  errors: number;
  first_error: string | null;
}

/** ON-DEMAND single-stock news refresh (2026-09-22) -- the "Refresh news"
 * button. Replaces the old daily-cron rotation (removed from
 * daily_pipeline.py the same day): one HTTP call for exactly this stock,
 * safely synchronous, no async job polling needed. */
export async function refreshInstrumentNews(token: string, securityId: string): Promise<NewsRefreshResultDto> {
  const res = await fetch(`/api/instrument/${encodeURIComponent(securityId)}/news/refresh`, {
    method: "POST", headers: authHeaders(token),
  });
  return unwrap<NewsRefreshResultDto>(res);
}

// ---------------------------------------------------------------------------
// On-demand chart indicators (app/api/routes/indicators.py --
// docs/technical_indicators_strategy.md's Tier-3 "compute on call" plan).
// 59 TA-Lib indicators (Overlap/Momentum/Volatility/Volume/Cycle groups),
// discovered live from GET /instrument/indicators/catalog -- not a static
// frontend list, same discipline as the Stocks page's filterable-fields.
// ---------------------------------------------------------------------------

export interface IndicatorCatalogEntry {
  name: string;
  display_name: string;
  group: string;
  overlay: boolean;
  inputs: string[];
  output_names: string[];
  default_params: Record<string, number>;
}

export async function fetchIndicatorCatalog(token: string): Promise<IndicatorCatalogEntry[]> {
  const res = await fetch("/api/instrument/indicators/catalog", { headers: authHeaders(token) });
  return unwrap<IndicatorCatalogEntry[]>(res);
}

export interface IndicatorPointDto {
  date: string;
  values: Record<string, number | null>;
}

export interface IndicatorSeriesDto {
  name: string;
  overlay: boolean;
  output_names: string[];
  params: Record<string, number>;
  series: IndicatorPointDto[];
}

export async function fetchIndicatorSeries(
  token: string,
  securityId: string,
  name: string,
  params: Record<string, number>,
  asOf?: string,
  barsLookbackDays = 180,
): Promise<IndicatorSeriesDto> {
  const qs = new URLSearchParams({
    name,
    params: JSON.stringify(params),
    bars_lookback_days: String(barsLookbackDays),
  });
  if (asOf) qs.set("as_of", asOf);
  const res = await fetch(`/api/instrument/${encodeURIComponent(securityId)}/indicator?${qs}`, {
    headers: authHeaders(token),
  });
  return unwrap<IndicatorSeriesDto>(res);
}

// ---------------------------------------------------------------------------
// Market / Indices (app/api/routes/market.py, app/api/routes/indices.py).
// THE INDEX RESOLUTION, carried into the client types: an index has a real
// sigma (the persistence convention, a literal magnitude -- NOT rank-space,
// the opposite rule from stock sigma) but NO directional signal --
// `no_directional_signal` is always true and `no_directional_signal_note`
// carries the real reason (0/42 blessed features, 4 pilots). Never render a
// direction arrow/ring for an index; there is no `predicted_return` field
// here at all, by design.
// ---------------------------------------------------------------------------

export interface IndexCardDto {
  security_id: string;
  display_ticker: string | null;
  as_of: string;
  // The actual date of `close` -- may lag `as_of` if this index's own bar
  // hasn't landed for the requested date. Use THIS, not `as_of`, for a
  // per-block "as of" label on anything derived from `close`/`day_change_pct`.
  bar_date: string | null;
  close: number | null;
  prev_close: number | null;
  day_change_pct: number | null;
  no_directional_signal: true;
  no_directional_signal_note: string;
  // ui_spec A6's three vol lenses -- labelled and never blended. realized_vol
  // and predicted_sigma are FRACTIONS (0.123 = 12.3%, x100 to display).
  // implied_vol (India VIX) is already a real index level/percentage number
  // (12.16 means 12.16%) -- do NOT x100 it, or NIFTY-only, always null on
  // every other index (there is no BSE-equivalent VIX).
  realized_vol: number | null;
  predicted_sigma: number | null;
  implied_vol: number | null;
  sigma_is_rank_space: false;
  sigma_regime_status: "ADEQUATE" | "DEGRADED" | "REGIME_BREAK" | "NO_DATA";
  sigma_regime_note: string | null;
  // The model_performance window's own as_of_date -- a third, independent
  // freshness date from `bar_date`/`as_of` (regime monitoring can lag both).
  sigma_regime_as_of: string | null;
}

export async function fetchMarketIndices(token: string, asOf?: string): Promise<IndexCardDto[]> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const res = await fetch(`/api/market/indices${qs}`, { headers: authHeaders(token) });
  return unwrap<IndexCardDto[]>(res);
}

export interface BreadthDto {
  available: boolean;
  date: string | null;
  advances: number | null;
  declines: number | null;
  unchanged: number | null;
  pct_above_200dma: number | null;
  pct_above_50dma: number | null;
  new_highs: number | null;
  new_lows: number | null;
}

export interface BreadthHistoryPointDto {
  date: string;
  net_adv_decl: number;
}

export interface FlowsDto {
  available: boolean;
  note: string;
  date: string | null;
  fii_net: number | null;
  dii_net: number | null;
}

export interface FlowsPointDto {
  date: string;
  fii_net: number | null;
  dii_net: number | null;
  india_vix: number | null;
  vix_change: number | null;
  fii_cum: number | null;
  dii_cum: number | null;
  fii_minus_dii: number | null;
  fii_z: number | null;
  dii_z: number | null;
  vix_change_z: number | null;
  rolling_corr_fii: number | null;
  rolling_corr_dii: number | null;
  rolling_corr_n: number | null;
}

export interface FlowsCorrelationDto {
  sample_n: number;
  corr_vix_fii: number | null;
  corr_vix_dii: number | null;
  corr_vix_fii_minus_dii: number | null;
  note: string;
}

export interface MacroEventDto {
  event_date: string;
  event_type: string | null;
  description: string | null;
  country: string | null; // "IN" or "US", parsed from event_type's own prefix
}

export interface ValuationDto {
  available: boolean;
  note: string;
  index_id: string;
  pe: number | null;
  pb: number | null;
  div_yield: number | null;
  //: The actual date pe/pb/div_yield were published for -- can lag the
  //: page's own as_of (NSE Indices' own publish lag).
  as_of_date: string | null;
  //: mean/SD/n of pe over ONLY the consolidated-earnings-basis window
  //: (see methodology_note) -- null when there isn't enough history yet.
  mean_pe: number | null;
  sd_pe: number | null;
  band_n: number;
  //: "cheap" | "fair" | "expensive" | null
  classification: "cheap" | "fair" | "expensive" | null;
  //: Always present when available -- the April-2021 standalone-to-
  //: consolidated earnings-basis switch, stated plainly.
  methodology_note: string | null;
  //: The full post-2021 (date, pe) series -- the SAME points mean_pe/sd_pe
  //: were computed from, for the valuation-band chart (2026-09-27).
  pe_series: { date: string; pe: number }[];
}

export interface CapePointDto {
  date: string;
  cape: number;
  //: The expanding fair-value band's mean/SD AS OF THIS POINT (computed
  //: from history strictly before it) -- null until enough points have
  //: accumulated in this segment.
  band_mean: number | null;
  band_sd: number | null;
  //: The actual trailing-EPS smoothing window length behind this point,
  //: in trading days (shows whether the full 10-year/2520-day cap has
  //: been reached, or this is still an expanding, shorter window).
  window_days: number;
}

export interface CapeSegmentDto {
  available: boolean;
  reason: string | null;
  points: CapePointDto[];
  window_days_final: number;
  segment_start: string | null;
  segment_end: string | null;
}

/** CAPE fair-value-range chart data -- a SEPARATE chart from ValuationDto's
 * static mean/SD band, never merged into it. NOMINAL (not inflation-
 * adjusted -- no India CPI series exists in this system) and computed as
 * TWO segments split at the 2021-04-01 earnings-basis switch (never one
 * spliced line) -- see backend app/factory/index_cape.py's module
 * docstring for the full methodology. */
export interface CapeDto {
  index_id: string;
  available: boolean;
  note: string | null;
  nominal_note: string | null;
  segmentation_note: string | null;
  pre: CapeSegmentDto | null;
  post: CapeSegmentDto | null;
}

export async function fetchIndexCapeDetail(
  token: string,
  securityId: string,
): Promise<CapeDto> {
  const res = await fetch(`/api/indices/${encodeURIComponent(securityId)}/cape`, {
    headers: authHeaders(token),
  });
  return unwrap<CapeDto>(res);
}

export interface StockCapePointDto {
  date: string;
  cape: number;
  band_mean: number | null;
  band_sd: number | null;
  window_days: number;
}

export interface BandStabilityDto {
  status: "mean_reverting" | "trending";
  note: string | null;
}

export interface PersistentBreakoutDto {
  direction: "above" | "below";
  fraction: number;
  note: string;
}

/** Relative CAPE band for one STOCK -- distinct from CapeDto (indices):
 * single unsegmented series (no 2021 earnings-basis break at the
 * individual-company level, verified empirically -- see backend
 * app/factory/stock_cape.py), and the band is THIS STOCK'S OWN historical
 * CAPE range (absorbs its structural growth/quality premium) rather than
 * any absolute/universal scale -- a growth stock at its normal premium
 * reads "fair", not "expensive". */
export interface StockCapeDto {
  security_id: string;
  available: boolean;
  reason: string | null;
  //: "tier1" (>=10yr CAPE band), "tier2" (5-10yr CAPE band, lower
  //: confidence), "tier3" (sector-premium band -- no CAPE possible, e.g.
  //: recent IPOs), or null if unavailable.
  tier: "tier1" | "tier2" | "tier3" | null;
  //: Tier-specific caveat: null for tier1, a shorter-window disclaimer for
  //: tier2, the sector-premium methodology note for tier3.
  tier_note: string | null;
  nominal_note: string | null;
  ttm_note: string | null;
  points: StockCapePointDto[];
  window_days_final: number;
  series_start: string | null;
  series_end: string | null;
  current_cape: number | null;
  band_mean: number | null;
  band_sd: number | null;
  classification: "cheap" | "fair" | "expensive" | null;
  band_stability: BandStabilityDto | null;
  persistent_breakout: PersistentBreakoutDto | null;
}

export async function fetchStockCapeDetail(
  token: string,
  securityId: string,
): Promise<StockCapeDto> {
  const res = await fetch(`/api/instrument/${encodeURIComponent(securityId)}/stock-cape`, {
    headers: authHeaders(token),
  });
  return unwrap<StockCapeDto>(res);
}

export interface SourceHealthDto {
  source: string;
  status: string;
}

export interface MarketOverviewDto {
  as_of: string;
  // Calendar/pipeline-schedule-aware freshness reference -- use for every
  // AsOfBadge's `reference` on this page, never `as_of` on its own before
  // the pipeline's 19:00 IST run or all weekend.
  expected_as_of: string;
  breadth: BreadthDto;
  breadth_history: BreadthHistoryPointDto[];
  flows: FlowsDto;
  flows_series: FlowsPointDto[];
  flows_series_note: string;
  flows_correlation: FlowsCorrelationDto;
  macro_upcoming: MacroEventDto[];
  macro_recent: MacroEventDto[];
  valuation: ValuationDto;
  cape: CapeDto;
  benchmarks: IndexCardDto[];
  source_health: SourceHealthDto[];
}

export async function fetchMarketOverview(token: string, asOf?: string): Promise<MarketOverviewDto> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const res = await fetch(`/api/market/overview${qs}`, { headers: authHeaders(token) });
  return unwrap<MarketOverviewDto>(res);
}

// "Strongest momentum" -- ranks the 17 INDICES (never stocks) by real price
// return over N trading days. See app/api/routes/market.py's own comment.
export interface MomentumRowDto {
  security_id: string;
  display_ticker: string | null;
  return_pct: number | null;
}

export interface MomentumTimeframeDto {
  label: string;
  days: number;
  ranked: MomentumRowDto[];
}

export interface MomentumDto {
  as_of: string;
  timeframes: MomentumTimeframeDto[];
}

export async function fetchMarketMomentum(token: string, asOf?: string): Promise<MomentumDto> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const res = await fetch(`/api/market/momentum${qs}`, { headers: authHeaders(token) });
  return unwrap<MomentumDto>(res);
}

// V2 market news RSS feed (app/api/routes/market_news.py) -- DISPLAY-ONLY.
// Narrowed slice of docs/v2/userstories_news_feed.md: general-news RSS only,
// no NSE/BSE announcements, no ticker mapping, no priced-in read, no agentic
// wiring. No score/sentiment field anywhere in this shape, by design.
export interface MarketNewsItemDto {
  item_id: number;
  source: string;
  title: string;
  url: string;
  published_at: string;
  summary: string | null;
  summary_provider: string | null;
}

export interface MarketNewsFeedStatusDto {
  source: string;
  display_name: string;
  status: "fresh" | "stale" | "unreachable";
  newest_item_age_minutes: number | null;
  last_checked_at: string | null;
}

export interface MarketNewsDto {
  items: MarketNewsItemDto[];
  feed_status: MarketNewsFeedStatusDto[];
}

export async function fetchMarketNews(token: string, limit = 40): Promise<MarketNewsDto> {
  const res = await fetch(`/api/market-news?limit=${limit}`, { headers: authHeaders(token) });
  return unwrap<MarketNewsDto>(res);
}

/** This index's OWN P/E/P/B/div-yield + its own post-2021 mean/SD band,
 * PLUS its current P/E premium/discount to NIFTY's own P/E and THAT
 * ratio's own post-2021 band -- "cheap vs its own history" and "cheap vs
 * the market right now" are genuinely different questions and can
 * disagree. `pe_relative_to_nifty`/`vs_nifty_classification` are always
 * null for IDX:NIFTY itself. `available=false` is the honest state for
 * IDX:SENSEX (BSE, no NSE Indices source) or any not-yet-captured index. */
export interface IndexValuationDto {
  available: boolean;
  note: string;
  index_id: string;
  pe: number | null;
  pb: number | null;
  div_yield: number | null;
  as_of_date: string | null;
  mean_pe: number | null;
  sd_pe: number | null;
  band_n: number;
  classification: "cheap" | "fair" | "expensive" | null;
  methodology_note: string | null;
  //: `[]` on the screener/cross-sector LIST endpoints (kept light on
  //: purpose) -- populated only by fetchIndexValuationDetail below.
  pe_series: { date: string; pe: number }[];
  pe_relative_to_nifty: number | null;
  pe_relative_mean: number | null;
  pe_relative_sd: number | null;
  pe_relative_n: number;
  vs_nifty_classification: "cheap" | "fair" | "expensive" | null;
}

/** ONE index's full valuation WITH its post-2021 P/E series -- the
 * valuation-band chart's data source, fetched lazily on demand rather
 * than embedded in the bulk list endpoints. */
export async function fetchIndexValuationDetail(
  token: string,
  securityId: string,
  asOf?: string,
): Promise<IndexValuationDto> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const res = await fetch(`/api/indices/${encodeURIComponent(securityId)}/valuation${qs}`, {
    headers: authHeaders(token),
  });
  return unwrap<IndexValuationDto>(res);
}

export interface IndexRowDto {
  security_id: string;
  display_ticker: string | null;
  bar_date: string | null;
  close: number | null;
  day_change_pct: number | null;
  features: Record<string, number | null>;
  no_directional_signal: true;
  no_directional_signal_note: string;
  predicted_sigma: number | null;
  sigma_regime_status: "ADEQUATE" | "DEGRADED" | "REGIME_BREAK" | "NO_DATA";
  sigma_regime_note: string | null;
  sigma_regime_as_of: string | null;
  constituent_breadth_pct: number | null;
  constituent_count: number | null;
  valuation: IndexValuationDto;
}

export interface IndicesFilterRequest {
  groups: ConditionGroup[];
  group_op: "AND" | "OR";
  extra_columns: string[];
  limit: number;
  offset: number;
}

export interface IndicesFilterResponse {
  as_of: string;
  data_as_of: string | null;
  // Calendar/pipeline-schedule-aware freshness reference -- use for
  // AsOfBadge's `reference`, never `as_of` on its own.
  expected_as_of: string;
  total_universe: number;
  matched: number;
  coverage_report: string;
  rows: IndexRowDto[];
}

export async function fetchIndices(
  token: string,
  body: IndicesFilterRequest,
  asOf?: string,
): Promise<IndicesFilterResponse> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const res = await fetch(`/api/indices${qs}`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return unwrap<IndicesFilterResponse>(res);
}

/** The sector-rotation-via-valuation view: every index's P/E vs its OWN
 * history AND vs NIFTY's, in one page. */
export interface CrossSectorValuationRowDto {
  security_id: string;
  display_ticker: string | null;
  valuation: IndexValuationDto;
}

export interface CrossSectorValuationResponseDto {
  as_of: string;
  expected_as_of: string;
  rows: CrossSectorValuationRowDto[];
  methodology_note: string;
  composition_drift_note: string;
}

export async function fetchCrossSectorValuation(
  token: string,
  asOf?: string,
): Promise<CrossSectorValuationResponseDto> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const res = await fetch(`/api/indices/valuation${qs}`, { headers: authHeaders(token) });
  return unwrap<CrossSectorValuationResponseDto>(res);
}

// ---------------------------------------------------------------------------
// Evaluation & Champions (app/api/routes/evaluation.py, ui_spec H1-H4)
// ---------------------------------------------------------------------------

export interface SlotSummaryDto {
  slot_key: string;
  level: string;
  output_type: string;
  champion_model_id: string | null;
  champion_model_kind: string | null;
  n_contenders: number;
  evidence_speed: string;
  monitored_not_raced: boolean;
  latest_metric_name: string | null;
  latest_metric_value: number | null;
  sample_n: number | null;
  pending_n: number | null;
  // V2 Live Forward Scorecard delta (userstories_scorecard.md US-D2) -- the
  // slot's own high-conviction bucket only; null when scorecard_fire has no
  // genuinely-live data for this slot yet, or it isn't a stock/return slot.
  scorecard_realized_ic: number | null;
  scorecard_expected_ic: number | null;
  scorecard_status: string | null;
}

export async function fetchEvaluationSlots(token: string, window = 60): Promise<SlotSummaryDto[]> {
  const res = await fetch(`/api/evaluation/slots?window=${window}`, { headers: authHeaders(token) });
  return unwrap<SlotSummaryDto[]>(res);
}

// ---------------------------------------------------------------------------
// Live Forward Scorecard (app/api/routes/scorecard.py, userstories_scorecard.md)
// ---------------------------------------------------------------------------

export interface ScorecardBucketDto {
  segment_key: string;
  conviction: "high" | "mixed" | "single" | null;
  horizon: number;
  n_dates_scored: number;
  n_blocks: number;
  n_fires: number;
  realized_ic_mean: number | null;
  ci_low: number | null;
  ci_high: number | null;
  expected_ic: number | null;
  expected_n_folds: number | null;
  p_fdr: number | null;
  hit_rate: number | null;
  net_of_benchmark: number | null;
  status: string;
}

export interface ScorecardResponseDto {
  min_independent_blocks: number;
  buckets: ScorecardBucketDto[];
  note: string;
  ci_method: string;
  benchmark: string;
}

export async function fetchForwardScorecard(token: string): Promise<ScorecardResponseDto> {
  const res = await fetch("/api/scorecard", { headers: authHeaders(token) });
  return unwrap<ScorecardResponseDto>(res);
}

export interface ContenderDto {
  model_id: string;
  version: string;
  model_kind: string;
  is_champion: boolean;
  metric_name: string | null;
  metric_value: number | null;
  sample_n: number | null;
  pending_n: number | null;
}

export interface PromotionVerdictDto {
  status: "no_challenger" | "insufficient_evidence" | "not_significant" | "behind" | "promotable";
  message: string;
  n_independent_blocks: number | null;
  dm_stat: number | null;
  dm_p: number | null;
  mean_diff: number | null;
}

export interface SlotDetailDto {
  slot_key: string;
  level: string;
  output_type: string;
  horizon: number | null;
  evidence_speed: string;
  monitored_not_raced: boolean;
  contenders: ContenderDto[];
  verdict: PromotionVerdictDto;
}

export async function fetchSlotDetail(token: string, slotKey: string, window = 60): Promise<SlotDetailDto> {
  const res = await fetch(`/api/evaluation/slots/${encodeURIComponent(slotKey)}?window=${window}`, {
    headers: authHeaders(token),
  });
  return unwrap<SlotDetailDto>(res);
}

// ---------------------------------------------------------------------------
// Model Detail (app/api/routes/models.py, ui_spec H5)
// ---------------------------------------------------------------------------

export interface LedgerRowDto {
  run_date: string;
  security_id: string;
  display_ticker: string | null;
  predicted_value: number | null;
  realized_value: number | null;
  error: number | null;
  resolved: boolean;
}

export interface WindowMetricDto {
  window_days: number;
  metric_name: string;
  metric_value: number;
  sample_n: number;
  pending_n: number;
}

export interface PerfPointDto {
  as_of_date: string;
  metric_value: number;
  sample_n: number;
  pending_n: number;
}

export interface ModelDetailDto {
  model_id: string;
  version: string;
  model_kind: string;
  level: string;
  target_ref: string;
  output_type: string;
  horizon: number | null;
  slot_key: string | null;
  is_champion: boolean;
  enabled: boolean;
  pkl_path: string | null;
  features: { key: string; label: string | null; kind: string | null; importance: number | null; rank: number | null; importance_source: string | null }[];
  features_note: string | null;
  sample_n: number;
  pending_n: number;
  training_run: Record<string, unknown> | null;
  windows: WindowMetricDto[];
  metric_series: PerfPointDto[];
  ledger: LedgerRowDto[];
}

export async function fetchModelDetail(
  token: string,
  modelId: string,
  version = "1",
  limit = 500,
): Promise<ModelDetailDto> {
  const res = await fetch(
    `/api/models/${encodeURIComponent(modelId)}/detail?version=${encodeURIComponent(version)}&limit=${limit}`,
    { headers: authHeaders(token) },
  );
  return unwrap<ModelDetailDto>(res);
}

// ---------------------------------------------------------------------------
// Dashboard (app/api/routes/dashboards.py, ui_spec H6/I1)
// ---------------------------------------------------------------------------

export interface DashboardLeaderboardRowDto {
  model_id: string;
  version: string;
  level: string;
  target_ref: string;
  slot_key: string | null;
  output_type: string;
  is_champion: boolean;
  monitored_not_raced: boolean;
  metric_name: string | null;
  metric_value: number | null;
  sample_n: number | null;
  pending_n: number | null;
}

export interface DashboardKpisDto {
  active_champions: number;
  avg_stock_return_ic: number | null;
  avg_index_sigma_qlike: number | null;
  total_resolved: number;
  open_challenges: number;
  challenges_note: string;
}

export interface LevelRollupDto {
  level: string;
  metric_name: string;
  n_models: number;
  mean_metric_value: number | null;
  total_sample_n: number;
  total_pending_n: number;
}

export interface DashboardSummaryDto {
  window_days: number;
  kpis: DashboardKpisDto;
  leaderboard: DashboardLeaderboardRowDto[];
  level_rollups: LevelRollupDto[];
  decay_watch_available: boolean;
  decay_watch_note: string;
}

export async function fetchDashboardSummary(token: string, window = 60): Promise<DashboardSummaryDto> {
  const res = await fetch(`/api/dashboards/summary?window=${window}`, { headers: authHeaders(token) });
  return unwrap<DashboardSummaryDto>(res);
}

// ---------------------------------------------------------------------------
// Data console (app/api/routes/console.py, ui_spec A8 / Part B pages 10-11).
// READ-ONLY: Run-now/Re-fetch/Backfill have no endpoint here by design --
// this is the Phase 7 write-path this build does not implement.
// ---------------------------------------------------------------------------

export interface SourceStatusDto {
  source: string;
  status: string | null;
  last_good_date: string | null;
  gap_count: number | null;
  // Extended sources (the ~10 pipelines added alongside the original 4 --
  // see backend/app/api/console_sources.py) carry these; the 4 legacy
  // source_stats-backed rows leave them null/false.
  label?: string | null;
  cadence?: string | null;
  days_since_last_data?: number | null;
  row_count?: number | null;
  last_run_at?: string | null;
  last_run_status?: string | null;
  run_now_note?: string | null;
  extended?: boolean;
}

export async function fetchConsoleSources(token: string): Promise<SourceStatusDto[]> {
  const res = await fetch(`/api/console/sources`, { headers: authHeaders(token) });
  return unwrap<SourceStatusDto[]>(res);
}

export interface ScheduledJobDto {
  key: string;
  label: string;
  desc: string;
  cadence: string;
  freq_label: string;
  status: "HEALTHY" | "DUE_SOON" | "OVERDUE" | "UNKNOWN";
  reason: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
  note: string | null;
}

export interface ScheduledJobsOut {
  as_of: string;
  jobs: ScheduledJobDto[];
  counts: Record<string, number>;
}

export async function fetchScheduledJobs(token: string): Promise<ScheduledJobsOut> {
  const res = await fetch(`/api/scheduled-jobs`, { headers: authHeaders(token) });
  return unwrap<ScheduledJobsOut>(res);
}

export interface SeriesStatusDto {
  source: string;
  series_key: string;
  display_ticker: string | null;
  company_name: string | null;
  status: string | null;
  last_good_date: string | null;
  gap_count: number | null;
  row_count: number | null;
  coverage_start: string | null;
  coverage_end: string | null;
  recent_series: { date: string; close: number }[] | null;
}

export async function fetchConsoleSeries(token: string, source: string, limit = 50): Promise<SeriesStatusDto[]> {
  const res = await fetch(`/api/console/sources/${encodeURIComponent(source)}/series?limit=${limit}`, {
    headers: authHeaders(token),
  });
  return unwrap<SeriesStatusDto[]>(res);
}

export interface IngestionLogRowDto {
  run_id: string;
  source: string;
  run_at: string;
  triggered_by: string;
  status: string;
  error_count: number;
  first_error: string | null;
  rows_attempted: number | null;
  rows_inserted: number | null;
  rows_updated: number | null;
}

export async function fetchIngestionLog(token: string, source?: string, limit = 30): Promise<IngestionLogRowDto[]> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (source) qs.set("source", source);
  const res = await fetch(`/api/console/ingestion-log?${qs}`, { headers: authHeaders(token) });
  return unwrap<IngestionLogRowDto[]>(res);
}

export interface RawBarRowDto {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  source: string;
  ingested_at: string;
}

export async function fetchRawBars(token: string, securityId: string, limit = 100): Promise<RawBarRowDto[]> {
  const res = await fetch(`/api/console/raw/bars?security_id=${encodeURIComponent(securityId)}&limit=${limit}`, {
    headers: authHeaders(token),
  });
  return unwrap<RawBarRowDto[]>(res);
}

export interface QuarantineRowDto {
  date: string;
  security_id: string;
  source: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  failed_checks: { check: string; detail: string }[];
  reason: string;
  ingested_at: string;
}

export async function fetchRawQuarantine(token: string, securityId: string, limit = 100): Promise<QuarantineRowDto[]> {
  const res = await fetch(`/api/console/raw/quarantine?security_id=${encodeURIComponent(securityId)}&limit=${limit}`, {
    headers: authHeaders(token),
  });
  return unwrap<QuarantineRowDto[]>(res);
}

// ---------------------------------------------------------------------------
// Review queues (app/api/routes/queues.py, ui_spec Part B page 12).
// READ-ONLY: confirm/reject/link/attribute/save-mapping have no endpoint --
// this surface's whole point, flagged as the key remaining write-path.
// ---------------------------------------------------------------------------

export interface QueueResponseDto<T> {
  total_pending: number;
  rows: T[];
}

export interface CaReviewRowDto {
  queue_id: number;
  ca_id: number | null;
  security_id: string;
  display_ticker: string | null;
  company_name: string | null;
  ex_date: string;
  action_type: string | null;
  yf_ratio: number | null;
  observed_ratio: number | null;
  proposed_factor: number | null;
  breeze_state: string | null;
  ratios_disagree: boolean;
  detected_by: string | null;
  status: string;
  classification: string | null;
  notes: string | null;
  created_at: string;
}

export async function fetchCaReviewQueue(token: string, limit = 100, offset = 0): Promise<QueueResponseDto<CaReviewRowDto>> {
  const res = await fetch(`/api/queues/ca-review?limit=${limit}&offset=${offset}`, { headers: authHeaders(token) });
  return unwrap<QueueResponseDto<CaReviewRowDto>>(res);
}

export interface ReconciliationRowDto {
  queue_id: number;
  security_id: string;
  display_ticker: string | null;
  company_name: string | null;
  isin: string;
  code_type: string;
  old_value: string;
  new_value: string;
  event_type: string | null;
  status: string;
  detected_at: string;
  notes: string | null;
}

export async function fetchReconciliationQueue(token: string, limit = 100, offset = 0): Promise<QueueResponseDto<ReconciliationRowDto>> {
  const res = await fetch(`/api/queues/reconciliation?limit=${limit}&offset=${offset}`, { headers: authHeaders(token) });
  return unwrap<QueueResponseDto<ReconciliationRowDto>>(res);
}

export interface UnmatchedMappingRowDto {
  queue_id: number;
  isin: string;
  display_ticker: string | null;
  company_name: string | null;
  attempted_symbol: string | null;
  target_source: string;
  reason: string;
  status: string;
  created_at: string;
}

export async function fetchUnmatchedMappingsQueue(token: string, limit = 100, offset = 0): Promise<QueueResponseDto<UnmatchedMappingRowDto>> {
  const res = await fetch(`/api/queues/unmatched-mappings?limit=${limit}&offset=${offset}`, { headers: authHeaders(token) });
  return unwrap<QueueResponseDto<UnmatchedMappingRowDto>>(res);
}

// --- Queue write-actions (2026-09-06) --------------------------------------
// CA-confirm RESTATES the security's adjusted price history (via as-of-T --
// nothing is written to `bars` itself, only the corporate_actions ledger,
// but every downstream read changes) and triggers a full recompute of its
// stored features. The most consequential write in the app.

export interface CaDecisionDto {
  queue_id: number;
  ca_id: number;
  security_id: string;
  display_ticker: string | null;
  company_name: string | null;
  ex_date: string;
  decision: "confirmed" | "rejected";
  adjustment_factor: number | null;
  snapped_ratio: number | null;
  note: string;
}

export async function confirmCa(token: string, queueId: number): Promise<CaDecisionDto> {
  const res = await fetch(`/api/queues/ca-review/${queueId}/confirm`, { method: "POST", headers: authHeaders(token) });
  return unwrap<CaDecisionDto>(res);
}

export async function rejectCa(token: string, queueId: number, reason?: string): Promise<CaDecisionDto> {
  const res = await fetch(`/api/queues/ca-review/${queueId}/reject`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify({ reason: reason ?? null }),
  });
  return unwrap<CaDecisionDto>(res);
}

export interface ReconciliationResolveDto {
  queue_id: number;
  security_id: string;
  event_type: "rename" | "demerger";
  old_value: string | null;
  new_value: string | null;
  security_master_updated: boolean;
}

export async function linkRename(token: string, queueId: number, newValue?: string): Promise<ReconciliationResolveDto> {
  const res = await fetch(`/api/queues/reconciliation/${queueId}/link-rename`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify({ new_value: newValue ?? null }),
  });
  return unwrap<ReconciliationResolveDto>(res);
}

export async function resolveDemerger(token: string, queueId: number, newValue?: string): Promise<ReconciliationResolveDto> {
  const res = await fetch(`/api/queues/reconciliation/${queueId}/demerger`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify({ new_value: newValue ?? null }),
  });
  return unwrap<ReconciliationResolveDto>(res);
}

export interface SaveMappingDto {
  queue_id: number;
  security_id: string;
  target_source: string;
  corrected_symbol: string;
}

export async function saveCorrectedMapping(
  token: string, queueId: number, securityId: string, correctedSymbol: string,
): Promise<SaveMappingDto> {
  const res = await fetch(`/api/queues/unmatched-mappings/${queueId}/save`, {
    method: "POST", headers: authHeaders(token),
    body: JSON.stringify({ security_id: securityId, corrected_symbol: correctedSymbol }),
  });
  return unwrap<SaveMappingDto>(res);
}

// ---------------------------------------------------------------------------
// Users (app/api/routes/users.py) -- read-only list.
// READ-ONLY: invite/resend/revoke/disable/re-enable have no endpoint --
// invite specifically needs Phase 10's email-link provisioning flow.
// ---------------------------------------------------------------------------

export interface UserRowDto {
  user_id: string;
  email: string;
  display_name: string | null;
  role: string;
  enabled: boolean;
  created_at: string | null;
  last_login_at: string | null;
}

export async function fetchUsers(token: string): Promise<UserRowDto[]> {
  const res = await fetch(`/api/users`, { headers: authHeaders(token) });
  return unwrap<UserRowDto[]>(res);
}

// ---------------------------------------------------------------------------
// Ensemble Builder (app/factory/ensemble.py + app/api/routes/models.py,
// ui_spec F6). Compose-only: components come from already-built models --
// no inline model-building here (F6.5).
// ---------------------------------------------------------------------------

export interface EnsembleSummaryDto {
  model_id: string;
  version: string;
  name: string | null;
  level: string;
  target_ref: string;
  output_type: string;
  horizon: number | null;
  slot_key: string | null;
  is_champion: boolean;
  n_components: number;
}

export async function fetchEnsembles(token: string): Promise<EnsembleSummaryDto[]> {
  const res = await fetch(`/api/models/ensembles`, { headers: authHeaders(token) });
  return unwrap<EnsembleSummaryDto[]>(res);
}

export interface ComponentDetailDto {
  child_kind: string;
  child_id: string;
  child_version: string;
  column_type: string;
  weight: number | null;
  weight_mode: string | null;
  added_at: string | null;
  latest_run_date: string | null;
  latest_mean_prediction: number | null;
  latest_n: number | null;
}

export interface BlendPreviewDto {
  available: boolean;
  reason: string | null;
  signal: number | null;
  confidence: number | null;
  n_components: number | null;
}

export interface EnsembleDetailDto {
  model_id: string;
  version: string;
  name: string | null;
  level: string;
  target_ref: string;
  output_type: string;
  horizon: number | null;
  slot_key: string | null;
  is_champion: boolean;
  components: ComponentDetailDto[];
  blend_preview: BlendPreviewDto;
}

export async function fetchEnsembleDetail(token: string, ensembleId: string, version = "1"): Promise<EnsembleDetailDto> {
  const res = await fetch(`/api/models/ensembles/${encodeURIComponent(ensembleId)}/detail?version=${encodeURIComponent(version)}`, {
    headers: authHeaders(token),
  });
  return unwrap<EnsembleDetailDto>(res);
}

export interface ModelSummaryDto {
  model_id: string;
  version: string;
  kind: string;
  model_kind: string;
  level: string;
  target_ref: string;
  output_type: string;
  horizon: number | null;
  slot_key: string | null;
  enabled: boolean;
  is_champion: boolean;
}

export async function fetchComponentEligibility(token: string, slotKey: string): Promise<ModelSummaryDto[]> {
  const res = await fetch(`/api/models/component-eligibility?slot_key=${encodeURIComponent(slotKey)}`, {
    headers: authHeaders(token),
  });
  return unwrap<ModelSummaryDto[]>(res);
}

export async function fetchAllModels(token: string): Promise<ModelSummaryDto[]> {
  const res = await fetch(`/api/models`, { headers: authHeaders(token) });
  return unwrap<ModelSummaryDto[]>(res);
}

// --- Model enable/disable + model_config save/load (Part B#7 write gap) ---

export interface SetEnabledResponseDto {
  model_id: string;
  version: string;
  enabled: boolean;
  is_champion: boolean;
  demoted_champion: boolean;
}

export async function setModelEnabled(
  token: string, modelId: string, enabled: boolean, version = "1",
): Promise<SetEnabledResponseDto> {
  const res = await fetch(`/api/models/${encodeURIComponent(modelId)}/enabled?version=${encodeURIComponent(version)}`, {
    method: "PATCH", headers: authHeaders(token), body: JSON.stringify({ enabled }),
  });
  return unwrap<SetEnabledResponseDto>(res);
}

export interface ModelConfigDto {
  config_id: string;
  user_id: string | null;
  name: string;
  setup: Record<string, unknown>;
  created_at: string;
}

export async function saveModelConfig(token: string, name: string, setup: Record<string, unknown>): Promise<ModelConfigDto> {
  const res = await fetch(`/api/models/configs`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify({ name, setup }),
  });
  return unwrap<ModelConfigDto>(res);
}

export async function listModelConfigs(token: string): Promise<ModelConfigDto[]> {
  const res = await fetch(`/api/models/configs`, { headers: authHeaders(token) });
  return unwrap<ModelConfigDto[]>(res);
}

export async function deleteModelConfig(token: string, configId: string): Promise<void> {
  const res = await fetch(`/api/models/configs/${encodeURIComponent(configId)}`, {
    method: "DELETE", headers: authHeaders(token),
  });
  if (!res.ok && res.status !== 204) {
    let detail = res.statusText;
    try {
      const b = (await res.json()) as { detail?: string };
      if (b.detail) detail = b.detail;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, detail);
  }
}

// --- Console write-actions: run-now / re-fetch / backfill (2026-09-06) ---
// All three are ASYNC JOBS (app/ingest/job_runner.py) -- a single yfinance
// fetch measured ~8s and run-now/backfill loop that across the whole active
// universe of a source, so the API returns a job_id immediately and the
// caller polls getConsoleJob until status leaves "running".

export interface ConsoleJobDto {
  job_id: string;
  kind: "run-now" | "refetch" | "backfill";
  params: Record<string, unknown>;
  triggered_by: string;
  status: "running" | "done" | "error";
  log: { ts: number; message: string }[];
  result: unknown;
  error: string | null;
}

export async function runSourceNow(token: string, source: string, securityIds?: string[]): Promise<ConsoleJobDto> {
  const res = await fetch(`/api/console/sources/${encodeURIComponent(source)}/run-now`, {
    method: "POST", headers: authHeaders(token),
    body: JSON.stringify({ security_ids: securityIds ?? null }),
  });
  return unwrap<ConsoleJobDto>(res);
}

export async function refetchSeries(token: string, source: string, securityId: string): Promise<ConsoleJobDto> {
  const res = await fetch(
    `/api/console/sources/${encodeURIComponent(source)}/series/${encodeURIComponent(securityId)}/refetch`,
    { method: "POST", headers: authHeaders(token) },
  );
  return unwrap<ConsoleJobDto>(res);
}

export async function backfillSeries(
  token: string, source: string, start: string, end: string, securityIds?: string[],
): Promise<ConsoleJobDto> {
  const res = await fetch(`/api/console/backfill`, {
    method: "POST", headers: authHeaders(token),
    body: JSON.stringify({ source, start, end, security_ids: securityIds ?? null }),
  });
  return unwrap<ConsoleJobDto>(res);
}

export async function getConsoleJob(token: string, jobId: string): Promise<ConsoleJobDto> {
  const res = await fetch(`/api/console/jobs/${encodeURIComponent(jobId)}`, { headers: authHeaders(token) });
  return unwrap<ConsoleJobDto>(res);
}

export interface CreateEnsembleRequest {
  ensemble_id: string;
  version?: string;
  name: string;
  level: string;
  target_ref: string;
  horizon: number;
  output_type: string;
}

export async function createEnsemble(token: string, body: CreateEnsembleRequest): Promise<EnsembleSummaryDto> {
  const res = await fetch(`/api/models/ensembles`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return unwrap<EnsembleSummaryDto>(res);
}

export interface AddComponentRequest {
  child_kind: "model" | "signal";
  child_id: string;
  child_version?: string;
  column_type: "regressor" | "classifier";
  weight_mode?: "learned" | "manual";
}

export async function addEnsembleComponent(
  token: string, ensembleId: string, body: AddComponentRequest, version = "1",
): Promise<{ ensemble_id: string; ensemble_version: string; child_kind: string; child_id: string; column_type: string }> {
  const res = await fetch(`/api/models/ensembles/${encodeURIComponent(ensembleId)}/components?version=${encodeURIComponent(version)}`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  return unwrap(res);
}

export async function removeEnsembleComponent(
  token: string, ensembleId: string, childKind: string, childId: string,
  version = "1", childVersion = "1",
): Promise<void> {
  const res = await fetch(
    `/api/models/ensembles/${encodeURIComponent(ensembleId)}/components/${encodeURIComponent(childKind)}/${encodeURIComponent(childId)}` +
    `?version=${encodeURIComponent(version)}&child_version=${encodeURIComponent(childVersion)}`,
    { method: "DELETE", headers: authHeaders(token) },
  );
  if (!res.ok && res.status !== 204) {
    let detail = res.statusText;
    try {
      const b = (await res.json()) as { detail?: string };
      if (b.detail) detail = b.detail;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, detail);
  }
}

// ---------------------------------------------------------------------------
// Feature Lab (app/api/routes/lab.py, ui_spec F4a) -- the UI front-end to the
// existing blessing pipeline in app/derived/lab.py. Scope cut, stated: macro
// features and the provisional-upload flow (F4a.7) are not built here.
// ---------------------------------------------------------------------------

export interface CandidateRowDto {
  feature_key: string;
  label: string | null;
  kind: string;
  blessed: boolean | null;
  verdict: string | null;
  ic: number | null;
  stability: string | null;
  coverage: number | null;
  blessed_at: string | null;
}

export async function fetchLabCandidates(token: string): Promise<CandidateRowDto[]> {
  const res = await fetch(`/api/lab/candidates`, { headers: authHeaders(token) });
  return unwrap<CandidateRowDto[]>(res);
}

export interface TargetSpecDto {
  type: "instrument" | "stocks_pooled" | "segment";
  security_id?: string | null;
  segment_key?: string | null;
}

export interface InvestigateRequestDto {
  feature_key: string;
  target: TargetSpecDto;
  horizon: number;
  window: "1y" | "3y" | "full";
}

export interface EdaStatsDto {
  n: number;
  mean: number;
  median: number;
  std: number;
  skew: number;
  excess_kurtosis: number;
  min: number;
  max: number;
  q1: number;
  q3: number;
  histogram: { bin_lo: number; bin_hi: number; count: number }[];
  n_missing_in_window: number;
}

export interface CrossSectionalResultDto {
  mean_ic: number | null;
  t_stat: number | null;
  n_dates: number;
}

export interface InvestigationResultDto {
  feature_key: string;
  target_label: string;
  verdict: "BLESS" | "MARGINAL" | "REJECT";
  reasons: string[];
  ic: number | null;
  stability: string | null;
  coverage: number | null;
  evidence: {
    rolling_ic?: number[];
    decile_means?: number[];
    decile_monotonicity?: number | null;
    n_obs?: number;
  };
  eda: EdaStatsDto;
  cross_sectional: CrossSectionalResultDto | null;
  contributors: {
    top_positive: { security_id: string; contribution: number }[];
    top_negative: { security_id: string; contribution: number }[];
    ic_excluding_top5: number | null;
    dates: number;
  } | null;
  ic_excluding_top5: number | null;
  lookahead_tested: boolean;
}

export async function investigateFeature(token: string, body: InvestigateRequestDto): Promise<InvestigationResultDto> {
  const res = await fetch(`/api/lab/investigate`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify(body),
  });
  return unwrap<InvestigationResultDto>(res);
}

export async function blessFeature(
  token: string, body: InvestigateRequestDto & { label?: string | null },
): Promise<{ feature_key: string; verdict: string; blessed: boolean }> {
  const res = await fetch(`/api/lab/bless`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify(body),
  });
  return unwrap(res);
}

export interface ControlResultDto {
  control: string;
  required_verdict: string;
  actual_verdict: string;
  passed: boolean;
  reasons: string[];
  ic: number | null;
}

export async function fetchLabControls(token: string): Promise<ControlResultDto[]> {
  const res = await fetch(`/api/lab/controls`, { headers: authHeaders(token) });
  return unwrap<ControlResultDto[]>(res);
}

// ---------------------------------------------------------------------------
// Model Builder (app/api/routes/model_builder.py, ui_spec F5) -- the UI
// front-end to the existing gated pipeline in app/factory/builder.py. Long-
// running (measured minutes-to-hours per build; see model_builder_service.py's
// module docstring), so this is job submit + poll, not a single request.
// ---------------------------------------------------------------------------

export interface SegmentOptionDto {
  segment_key: string;
  dimension: string;
  label: string | null;
  n_constituents: number;
}

export async function fetchBuilderSegments(token: string): Promise<SegmentOptionDto[]> {
  const res = await fetch(`/api/model-builder/segments`, { headers: authHeaders(token) });
  return unwrap<SegmentOptionDto[]>(res);
}

export interface IndexOptionDto {
  security_id: string;
  display_ticker: string | null;
}

export async function fetchBuilderIndices(token: string): Promise<IndexOptionDto[]> {
  const res = await fetch(`/api/model-builder/indices`, { headers: authHeaders(token) });
  return unwrap<IndexOptionDto[]>(res);
}

export interface TargetDeclareRequestDto {
  target_type: "return" | "volatility" | "direction";
  level: "stock" | "index";
  target_ref: string;
  n_classes?: number | null;
}

export interface TargetDeclareResponseDto {
  prediction_type: "classification" | "regression";
  families: Record<string, number[]>;
}

export async function declareTarget(token: string, body: TargetDeclareRequestDto): Promise<TargetDeclareResponseDto> {
  const res = await fetch(`/api/model-builder/target/declare`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify(body),
  });
  return unwrap<TargetDeclareResponseDto>(res);
}

export interface SubmitBuildRequestDto extends TargetDeclareRequestDto {
  horizon_family: string;
  training_start?: string | null;
}

export interface HorizonCurveResultDto {
  locked: number | null;
  shape: "PLATEAU" | "SPIKE" | "FLAT" | "BOUNDARY" | "DEAD" | "INSUFFICIENT";
  curve: Record<string, number | null>;
  peak?: number;
  neighbour_ratio?: number;
  spread?: number;
  reason: string;
  family: string;
}

export interface PruneResultDto {
  selected: string[];
  drops: Record<string, string>;
  vif: Record<string, number | null>;
  importance: Record<string, number | null>;
  min_importance: number;
  offered: string[];
}

export interface BakeoffFamilyResultDto {
  fold_scores: number[];
  mean_score: number;
  n_folds: number;
  metric: "hit_rate" | "rank_ic";
  is_winner: boolean;
}

export interface BakeoffResultDto {
  families: Record<string, BakeoffFamilyResultDto>;
  winner: string;
  prediction_type: "classification" | "regression";
  n_rows_used: number;
}

export interface PersistResultDto {
  model_id: string;
  version: string;
  pkl_path: string;
  family: string;
  horizon: number;
  slot_key: string;
  n_features: number;
  features: string[];
}

export interface BuildJobDto {
  job_id: string;
  spec: Record<string, unknown>;
  status: "running" | "awaiting_input" | "refused" | "error" | "done";
  stage: string;
  log: { ts: number; message: string }[];
  error: string | null;
  horizon_result: HorizonCurveResultDto | null;
  offered: string[] | null;
  prune_result: PruneResultDto | null;
  bakeoff_result: BakeoffResultDto | null;
  persist_result: PersistResultDto | null;
}

export async function submitBuild(token: string, body: SubmitBuildRequestDto): Promise<BuildJobDto> {
  const res = await fetch(`/api/model-builder/jobs`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify(body),
  });
  return unwrap<BuildJobDto>(res);
}

export async function fetchBuildJob(token: string, jobId: string): Promise<BuildJobDto> {
  const res = await fetch(`/api/model-builder/jobs/${encodeURIComponent(jobId)}`, { headers: authHeaders(token) });
  return unwrap<BuildJobDto>(res);
}

export async function lockHorizon(token: string, jobId: string, horizon: number): Promise<BuildJobDto> {
  const res = await fetch(`/api/model-builder/jobs/${encodeURIComponent(jobId)}/lock-horizon`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify({ horizon }),
  });
  return unwrap<BuildJobDto>(res);
}

export async function fetchOfferedFeatures(token: string, jobId: string): Promise<string[]> {
  const res = await fetch(`/api/model-builder/jobs/${encodeURIComponent(jobId)}/offered-features`, {
    headers: authHeaders(token),
  });
  return unwrap<string[]>(res);
}

export async function runPrune(
  token: string, jobId: string, selectedKeys: string[], excludedKeys: string[] = [],
): Promise<BuildJobDto> {
  const res = await fetch(`/api/model-builder/jobs/${encodeURIComponent(jobId)}/prune`, {
    method: "POST", headers: authHeaders(token),
    body: JSON.stringify({ selected_keys: selectedKeys, excluded_keys: excludedKeys }),
  });
  return unwrap<BuildJobDto>(res);
}

export async function runBakeoff(token: string, jobId: string): Promise<BuildJobDto> {
  const res = await fetch(`/api/model-builder/jobs/${encodeURIComponent(jobId)}/bakeoff`, {
    method: "POST", headers: authHeaders(token),
  });
  return unwrap<BuildJobDto>(res);
}

export async function persistModel(
  token: string, jobId: string, modelId: string, family: string | null,
): Promise<BuildJobDto> {
  const res = await fetch(`/api/model-builder/jobs/${encodeURIComponent(jobId)}/persist`, {
    method: "POST", headers: authHeaders(token),
    body: JSON.stringify({ model_id: modelId, family }),
  });
  return unwrap<BuildJobDto>(res);
}

// ---------------------------------------------------------------------------
// Simulation Workbench (app/api/routes/simulation.py, ui_spec Part B #9,
// data_spec §6.3) -- admin builds, any authenticated user views (U2). MC
// runs are synchronous (measured: worst case ~1.3s at 50k paths/252d,
// realistic defaults single-digit ms) -- no job/poll pattern needed here.
// ---------------------------------------------------------------------------

export interface DriftResolutionDto {
  available: boolean;
  mu_mean: number | null;
  mu_se: number | null;
  se_source: string | null;
  matched_horizon: number | null;
  contributing_segments: Record<string, { model_id: string; weight: number; predicted_value: number }>;
  gaps: string[];
  n_resolved_errors: number;
}

export interface VolResolutionDto {
  available: boolean;
  window: number;
  sigma_annual: number | null;
  as_of_date: string | null;
  rank_space_sigma_reference: number | null;
  rank_space_matched_horizon: number | null;
}

export interface EventResolutionDto {
  available: boolean;
  reason: string;
}

export interface VolRegimeDto {
  status: "NO_DATA" | "NORMAL" | "ELEVATED";
  percentile: number | null;
  note?: string;
  reason?: string;
}

export interface ReadinessDto {
  security_id: string;
  horizon_days: number;
  drift: DriftResolutionDto;
  vol: VolResolutionDto;
  event: EventResolutionDto;
  vol_regime: VolRegimeDto;
  n_forecasted: number;
  n_roles: number;
  ready: boolean;
}

export async function fetchReadiness(token: string, securityId: string, horizonDays: number, volWindow?: number): Promise<ReadinessDto> {
  const qs = new URLSearchParams({ horizon_days: String(horizonDays) });
  if (volWindow) qs.set("vol_window", String(volWindow));
  const res = await fetch(`/api/simulation/${encodeURIComponent(securityId)}/readiness?${qs}`, { headers: authHeaders(token) });
  return unwrap<ReadinessDto>(res);
}

export interface RunSimRequestDto {
  security_id: string;
  horizon_days: number;
  n_paths: number;
  steps_per_day?: number;
  distribution?: "normal" | "t5";
  seed?: number | null;
  vol_window_override?: number | null;
}

export interface SimCaveatDto {
  severity: "warning" | "info";
  role: string;
  message: string;
}

export interface SimBandsDto {
  days: number[];
  percentiles: Record<string, number[]>;
}

export interface RunSimResultDto {
  security_id: string;
  horizon_days: number;
  n_paths: number;
  distribution: string;
  seed: number | null;
  inputs: {
    drift: { mu_annual: number; mu_se_annual: number; se_source: string; mu_horizon_total: number; matched_horizon: number; contributing_segments: Record<string, unknown> };
    vol: { sigma_annual: number; window: number; as_of_date: string | null; rank_space_reference: number | null };
    event: EventResolutionDto;
  };
  bands: SimBandsDto;
  terminal_summary: { median_return_pct: number; p10_return_pct: number; p90_return_pct: number };
  caveats: SimCaveatDto[];
  vol_regime: VolRegimeDto;
  honesty_note: string;
}

export async function runSimulation(token: string, body: RunSimRequestDto): Promise<RunSimResultDto> {
  const res = await fetch(`/api/simulation/run`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify(body),
  });
  return unwrap<RunSimResultDto>(res);
}

export interface SaveSimRequestDto {
  security_id: string;
  name: string;
  component_models: Record<string, unknown>;
  reference_betas?: Record<string, unknown>;
  composition?: string;
  mc_params: Record<string, unknown>;
  horizon: number;
  provenance: Record<string, unknown>;
}

export async function saveSimulation(token: string, body: SaveSimRequestDto): Promise<{ sim_id: string }> {
  const res = await fetch(`/api/simulation/save`, {
    method: "POST", headers: authHeaders(token), body: JSON.stringify(body),
  });
  return unwrap(res);
}

export interface SavedSimSummaryDto {
  sim_id: string;
  name: string;
  horizon: number;
  composition: string;
  created_by: string;
  created_at: string;
}

export async function fetchSavedSims(token: string, securityId: string): Promise<SavedSimSummaryDto[]> {
  const res = await fetch(`/api/simulation/${encodeURIComponent(securityId)}/saved`, { headers: authHeaders(token) });
  return unwrap<SavedSimSummaryDto[]>(res);
}

export async function fetchSavedSimDetail(token: string, simId: string): Promise<RunSimResultDto & { sim_id: string; config: Record<string, unknown>; cached: boolean }> {
  const res = await fetch(`/api/simulation/saved/${encodeURIComponent(simId)}`, { headers: authHeaders(token) });
  return unwrap(res);
}

// -- Index MC (GET /simulation/index/{security_id}) -- a separate, always-on,
// read-only surface: no admin build step, no saved recipe (see
// app/factory/simulation_service.py's "INDEX MC" section) -- neutral drift
// (measured, not assumed: index_direction_pilot.py) + real persistence vol.
export interface IndexVolRegimeDto {
  status: "NO_DATA" | "ADEQUATE" | "DEGRADED" | "REGIME_BREAK";
  note?: string;
  reason?: string;
  monitored_qlike?: number;
  as_of_date?: string;
  safe_to_use_without_caveat: boolean;
}

export interface IndexSimResultDto {
  security_id: string;
  horizon_days: number;
  n_paths: number;
  distribution: string;
  seed: number | null;
  inputs: {
    drift: { mu_annual: number; mu_se_annual: number; se_source: string; basis: string };
    vol: { sigma_annual: number; window: number; as_of_date: string | null; convention: string };
  };
  bands: SimBandsDto;
  terminal_summary: { median_return_pct: number; p10_return_pct: number; p90_return_pct: number };
  caveats: SimCaveatDto[];
  vol_regime: IndexVolRegimeDto;
  honesty_note: string;
}

export async function fetchIndexSimulation(token: string, securityId: string, horizonDays: number, nPaths = 10_000): Promise<IndexSimResultDto> {
  const qs = new URLSearchParams({ horizon_days: String(horizonDays), n_paths: String(nPaths) });
  const res = await fetch(`/api/simulation/index/${encodeURIComponent(securityId)}?${qs}`, { headers: authHeaders(token) });
  return unwrap<IndexSimResultDto>(res);
}

// ---------------------------------------------------------------------------
// Coverage admin surface (app/api/routes/coverage.py, ui_spec Part G, G5) --
// Health / Segment grid / Stock resolver / Gaps / Reclassifications.
// Admin-only. The resolver wires the same /coverage/{id} endpoint that sat
// orphaned since Phase 6 -- its own response shape (contributing_segments
// as a DICT keyed by segment_key, not the array shape instrument.py's
// parallel path uses) is distinct from CoverageDetailDto above.
// ---------------------------------------------------------------------------

export interface CoverageHealthDto {
  universe_total: number;
  covered: number;
  breadth_pct: number;
  depth_distribution: Record<string, number>;
  segments_with_no_model: number;
  /** The two kinds "no champion" splits into (2026-09-25): a MEASURED
   * negative (a real build attempted, confirmed no signal) vs a segment
   * genuinely nobody has tried yet. Always sum to segments_with_no_model. */
  segments_tested_no_signal: number;
  segments_not_modelled: number;
  as_of: string;
}

export async function fetchCoverageHealth(token: string): Promise<CoverageHealthDto> {
  const res = await fetch(`/api/coverage/health`, { headers: authHeaders(token) });
  return unwrap<CoverageHealthDto>(res);
}

export interface SegmentGridRowDto {
  dimension: string;
  segment_key: string;
  label: string | null;
  n_constituents: number;
  has_model: boolean;
  model_id: string | null;
  horizon: number | null;
  stocks_covered: number;
  /** A MEASURED negative (a real build was attempted, at full rigour, and
   * confirmed no forecastable signal) -- never true when has_model=true. */
  tested_no_signal: boolean;
  no_signal_reason: string | null;
}

export async function fetchCoverageSegmentGrid(token: string): Promise<SegmentGridRowDto[]> {
  const res = await fetch(`/api/coverage/segment-grid`, { headers: authHeaders(token) });
  return unwrap<SegmentGridRowDto[]>(res);
}

export interface ThinlyCoveredStockDto {
  security_id: string;
  display_ticker: string | null;
  only_model: string | null;
  only_segment: string | null;
  missing_dimensions: string[];
}

export interface CoverageGapsDto {
  gaps_by_impact: SegmentGridRowDto[];
  /** Segments a real build attempt already confirmed carry no signal --
   * resolved negatives, kept separate from gaps_by_impact: these are not
   * "build this next" opportunities. */
  tested_no_signal: SegmentGridRowDto[];
  thinly_covered: ThinlyCoveredStockDto[];
  total_thinly_covered: number;
  shown_thinly_covered: number;
}

export async function fetchCoverageGaps(token: string, thinLimit = 200): Promise<CoverageGapsDto> {
  const res = await fetch(`/api/coverage/gaps?thin_limit=${thinLimit}`, { headers: authHeaders(token) });
  return unwrap<CoverageGapsDto>(res);
}

export interface ReclassificationRowDto {
  security_id: string;
  display_ticker: string | null;
  dimension: string;
  from_segment: string | null;
  to_segment: string | null;
  changed_on: string;
  consequence: string | null;
}

export async function fetchCoverageReclassifications(token: string, limit = 100): Promise<ReclassificationRowDto[]> {
  const res = await fetch(`/api/coverage/reclassifications?limit=${limit}`, { headers: authHeaders(token) });
  return unwrap<ReclassificationRowDto[]>(res);
}

export interface CoverageResolutionDto {
  security_id: string;
  output_type: "return" | "volatility";
  scale_note: string;
  signal: number | null;
  signal_skill_weighted: number | null;
  skill_delta: number | null;
  matched_horizon: number | null;
  contributing_segments: Record<string, { model_id: string; weight: number; weight_skill: number; ic: number | null; predicted_value: number }>;
  gaps: CoverageGapDto[];
  near_earnings: boolean;
  days_since_earnings: number | null;
  as_of: string;
  signal_as_of: string | null;
  secondary_horizons: HorizonSignalDictDto[];
  conviction: "high" | "mixed" | "single" | null;
}

export async function fetchCoverageResolution(token: string, securityId: string, outputType: "return" | "volatility"): Promise<CoverageResolutionDto> {
  const res = await fetch(`/api/coverage/${encodeURIComponent(securityId)}?output_type=${outputType}`, { headers: authHeaders(token) });
  return unwrap<CoverageResolutionDto>(res);
}

export interface ResearchFileMetaDto { file: string; present: boolean; as_of: string | null; stale: boolean | null }
export interface ResearchLibraryRowDto {
  feature: string;
  label: string | null;
  kind: string;
  blessed: boolean;
  best_target: string | null;
  ic: number | null;
  stability: string | null;
  stability_pos_share: number | null;
  coverage: string | null;
  blessed_at: string | null;
}
export interface ResearchSummaryDto {
  meta: { phase1: ResearchFileMetaDto; phase1b: ResearchFileMetaDto; blessing_gate: ResearchFileMetaDto; phase2_rolling: ResearchFileMetaDto };
  stock: { tests: number; fdr_relevant: number; placebo_tests: number; placebo_relevant: number; blessed_tests: number; blessed_relevant: number; unblessed_relevant: number } | null;
  distinct_effects: { raw_clears: number; distinct_effects: number; incremental_tests: number; adds_value: number; distinct_adds_value: number } | null;
  blessing_gate: { pairs: number; ship: number } | null;
  phase2: { tests: number; lead_relevant: number; unstable: number; underpowered: number; calibrated: boolean; placebo_white_noise: number; placebo_random_walk: number; placebo_tests_each: number } | null;
  library: { total: number; blessed: number; not_blessed: number; rows: ResearchLibraryRowDto[] };
}
export async function fetchResearchSummary(token: string): Promise<ResearchSummaryDto> {
  const res = await fetch(`/api/research/summary`, { headers: authHeaders(token) });
  return unwrap<ResearchSummaryDto>(res);
}

export interface FeatureUsageRowDto {
  model_id: string;
  version: string;
  level: string;
  target_ref: string;
  horizon: number | null;
  is_champion: boolean;
  n_features: number;
  rank: number | null;
}
export interface FeatureUsageDto {
  feature_key: string;
  source: string;
  checked: number;
  unreadable: number;
  models: FeatureUsageRowDto[];
}
export async function fetchFeatureUsage(token: string, featureKey: string): Promise<FeatureUsageDto> {
  const res = await fetch(`/api/models/by-feature/${encodeURIComponent(featureKey)}`, { headers: authHeaders(token) });
  return unwrap<FeatureUsageDto>(res);
}

// ---------------------------------------------------------------------------
// Execution-Stack Chain UI (app/api/routes/execution_stack.py, 2026-10-27) --
// admin-only. /book is a CACHE (weekly reconstruction, staleness-labelled);
// /risk is live, computed per-request from risk_book_snapshot.
// ---------------------------------------------------------------------------

export interface HeldPositionDto {
  security_id: string;
  display_ticker: string | null;
  sector_key: string | null;
  weight: number;
  sizing_final_size: number;
  signal_run_start_date: string | null;
  signal_age_trading_days: number | null;
  est_edge_remaining_pct: number | null;
  edge_remaining_note: string;
}

export interface ConstructionDecisionDto {
  security_id: string;
  display_ticker: string | null;
  sector_key: string | null;
  rank: number;
  sizing_final_size: number;
  decision: "admit" | "trim" | "drop" | "excluded";
  reason: string;
  rho_max: number | null;
  rho_max_effective: number | null;
  against_display_ticker: string | null;
  prior_only_pairs: string[];
  final_weight: number;
}

export interface ExecutionStackBookDto {
  reconstructed_as_of: string;
  days_since_reconstruction: number;
  held: HeldPositionDto[];
  decisions: ConstructionDecisionDto[];
  sector_exposure: Record<string, number>;
  sector_cap: number;
  per_name_cap: number;
  effective_breadth: number | null;
  deployed_pct: number;
  cash_buffer_pct: number;
  equal_weight_comparison: Record<string, number>;
  excluded_summary: Record<string, number>;
  universe_scanned: number | null;
  note: string;
}

export async function fetchExecutionStackBook(token: string): Promise<ExecutionStackBookDto> {
  const res = await fetch("/api/execution-stack/book", { headers: authHeaders(token) });
  return unwrap<ExecutionStackBookDto>(res);
}

export interface RiskTripRecordDto {
  as_of: string;
  drawdown_at_trip: number;
  equity_at_trip: number;
  peak_equity_at_trip: number;
}

export interface RiskStateDto {
  status: "not_started" | "tracking";
  as_of: string;
  days_tracked: number;
  state: "armed" | "tripped" | null;
  peak_equity: number | null;
  current_equity: number | null;
  drawdown_pct: number | null;
  exposure_multiplier: number | null;
  would_restore_multiplier: number | null;
  reason: string;
  trip_log: RiskTripRecordDto[];
  source_label: string;
}

export async function fetchExecutionStackRisk(token: string): Promise<RiskStateDto> {
  const res = await fetch("/api/execution-stack/risk", { headers: authHeaders(token) });
  return unwrap<RiskStateDto>(res);
}

export async function postRearm(token: string, note: string): Promise<RiskStateDto> {
  const res = await fetch("/api/execution-stack/risk/rearm", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ confirm: true, note }),
  });
  return unwrap<RiskStateDto>(res);
}

// --- Signal Half-Life (Track-A feature 1, 2026-10-27) ---
// Loosely typed on purpose, matching the backend's own stated
// simplification: a rich, nested, exploratory research structure for a
// single admin panel, not worth a full strict schema in v1.

export interface DecayCurvePoint {
  age: number;
  marginal_mean: number | null;
  marginal_ci_low: number | null;
  marginal_ci_high: number | null;
  cumulative_mean: number | null;
  cumulative_ci_low: number | null;
  cumulative_ci_high: number | null;
  n_dates: number;
}

export interface DecayShape {
  classification: string;
  half_life_days?: number | null;
  spearman?: number;
  r2?: number;
  fit_n?: number;
  breakpoint_age?: number;
  gap?: number;
  gap_ci_low?: number | null;
  gap_ci_high?: number | null;
}

export interface DecayPipelineResult {
  label: string;
  n_events: number;
  curve: Record<string, DecayCurvePoint>;
  tests: Record<string, any>;
  attrition: Record<string, any>;
  shape: DecayShape | null;
}

export interface SignalDecayDto extends DecayPipelineResult {
  computed_at: string;
  small_n_caveat: string;
  clean_benchmark_subset: DecayPipelineResult;
  benchmark_heterogeneity: {
    n_clean: number;
    n_fallback: number;
    fallback_events_by_sector: Record<string, number>;
  };
  same_stock_across_dates_overlap: {
    n_distinct_stocks: number;
    n_events: number;
    median_fires_per_stock: number | null;
    max_fires_per_stock: number | null;
    note: string;
  };
  cache_computed_at: string;
  cache_days_since_computed: number;
  cache_cadence_comparison: string;
}

export async function fetchSignalDecay(token: string): Promise<SignalDecayDto> {
  const res = await fetch("/api/execution-stack/signal-decay", { headers: authHeaders(token) });
  return unwrap<SignalDecayDto>(res);
}

// --- Daily Digest (Track-A feature 3/3, 2026-10-27) ---
// Loosely typed on purpose, same stated simplification as Signal
// Half-Life: a nested, exploratory aggregation of other modules' own
// state, for a single admin page, not worth a full strict schema in v1.

export interface DailyDigestDto {
  run_date: string;
  system_health: {
    run_date: string;
    top_status: string;
    top_label: string;
    pipeline: { status: string; detail: Record<string, any> | null };
    phase5: { status: string; detail: Record<string, any> | null };
    subsystems: {
      index: { status: string; reason: string; runway_trading_days_remaining: number;
               runway_threshold: number; predictions_today: number };
      stock: { status: string; fires_today: number; trailing_median: number | null;
               trailing_n_dates: number | null; reason: string };
    };
    gated_fire_date_count: number;
  };
  risk: {
    status: "not_started" | "tracking";
    reason?: string;
    as_of?: string;
    state?: "armed" | "tripped";
    drawdown_pct?: number;
    exposure_multiplier?: number;
    days_tracked?: number;
    early_reading_caveat?: boolean;
    early_reading_floor_days?: number;
    changed_since_yesterday?: boolean;
    change_note?: string | null;
    cumulative_return_pct?: number | null;
    vs_benchmark_pct?: number | null;
    equity_honesty_note?: string | null;
  };
  book_and_fires: {
    book: { status: string; book_as_of_date: string | null; book_age_trading_days: number | null;
            new_reconstruction_today: boolean; n_positions: number;
            book_date_mismatch?: boolean; book_date_mismatch_note?: string | null };
    signal_fires_today: {
      new_stock_count: number;
      new_stocks: { security_id: string; display_ticker: string }[];
      total_distinct_stocks_firing_today: number;
      note: string;
      cadence_note: string;
    };
    exits_since_last_reconstruction: { status: string; count: number | null; tickers: string[]; note: string };
    positions_by_signal_age: Record<string, number>;
    current_held_security_ids: string[];
  };
  half_life_status: string;
  snapshot_run_date: string;
  snapshot_computed_at: string;
}

export async function fetchDailyDigest(token: string): Promise<DailyDigestDto> {
  const res = await fetch("/api/execution-stack/daily-digest", { headers: authHeaders(token) });
  return unwrap<DailyDigestDto>(res);
}

// --- Forward-equity-record (2026-10-27) ---

export interface EquityCurvePoint {
  as_of_date: string;
  book_equity: number;
  benchmark_equity: number | null;
  equal_weight_equity: number | null;
  turnover_pct: number | null;
  source: "reconstructed" | "marked_forward";
}

export interface EquityCurveDto {
  status: "not_started" | "tracking";
  reason?: string;
  series: EquityCurvePoint[];
  days_tracked: number;
  early_reading_caveat: boolean;
  early_reading_floor_days: number;
  cumulative_return: { book: number; benchmark: number | null; equal_weight: number | null } | null;
  vs_benchmark_pct: number | null;
  latest_turnover: { pct: number; as_of: string; note: string } | null;
  honesty_note: string;
}

export async function fetchEquityCurve(token: string): Promise<EquityCurveDto> {
  const res = await fetch("/api/execution-stack/equity-curve", { headers: authHeaders(token) });
  return unwrap<EquityCurveDto>(res);
}
